/**
 * EventCatchUp — "Did you catch any of these?"
 *
 * The events that finished in the last week, each with the species the trainer
 * is still missing from it, as a row of checkboxes: tick what you caught, press
 * one button, done. That is the entire feature — the alternative it replaces is
 * opening the Dex, switching to the Shiny variant, searching a name and
 * scrolling, once per Pokémon, which is why the dex goes stale after every
 * Community Day.
 *
 * NO FORM. No CP, no source, no notes. Every extra field is a reason to do it
 * later, and "later" is the failure mode this card exists to fix. The write
 * goes through setManualWithLinks, which is the same path the manual add sheets
 * use — so a shiny tick cascades into the Main dex and syncs like any other
 * mark.
 *
 * TAPS CHOOSE; ONE BUTTON WRITES. A tap used to be the write itself, which is a
 * bad trade on a grid of small tiles inside a scrolling page — see
 * CatchUpEventBlock. Undo is available three ways after a commit, and every one
 * of them restores the exact prior state, cascade included.
 *
 * THE LIST IS FROZEN ON MOUNT. An added species stops being a gap, so a live
 * recompute would delete the tile the trainer just added — the list would
 * shrink under the finger and there would be nothing left to press undo on. The
 * gaps are computed once per event set and each tile carries its own state.
 * Recomputing is the NEXT visit's job.
 *
 * FREE FOR EVERYONE, deliberately, unlike the Calendar's forward-looking gap
 * strips. This one is data entry that makes the trainer's own dex better, which
 * every other surface in the app then reasons from; charging for it would be
 * charging for the privilege of giving us better data.
 */
import { megaFormForFormId } from "@/lib/megaDex";
import { speciesFormArtCandidates } from "@/lib/formSpriteCandidates";
import { DYNAMAX_FORM_ID } from "@/lib/dynamaxDex";
import { MEGA_EXTRA_FORMS, megaFormSpriteCandidates, megaGmaxSpriteCandidates, megaGmaxAtlasFormSlug, atlasFormSlugForFormId } from "@/lib/megaGmaxSprites";
import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Check, CalendarCheck } from "lucide-react";
import { decodeEventTextOptional } from "@shared/eventText";

import { CaughtSprite } from "@/components/CaughtSprite";
import { isDexVariantKey, variantAlpha, variantColor } from "@/components/dexVariantVisuals";
import { InfoBlock } from "@/components/InfoBlock";
import { EvoLinesToggle } from "@/components/EvoLinesToggle";
import { useEvoLines } from "@/lib/evoLinesPref";
import { expandSpawnsToLines } from "@/lib/eventEvoLines";
import { PokemonSilhouette } from "@/components/PokemonSilhouette";
import { useSession } from "@/components/SessionProvider";
import { speciesName } from "@/data/speciesNames";
import { getDexEntryState } from "@/lib/dexStateStore";
import { useGapCommit, type TileState } from "@/hooks/useGapCommit";
import {
  eventCatchUps,
  type CatchUpEntry,
  type CatchUpEventInput,
} from "@/lib/eventCatchUp";
import { raidVariantLabel, type RaidDexVariant } from "@/lib/raidDexCrossRef";
import { FORM_GROUPS_BY_FORM_ID } from "@/lib/generated/pokemonForms.generated";
import { isVariantAvailable } from "@/lib/variantAvailability";
import { isEventFormAvailable } from "@/lib/eventFormAvailability";
import { eventsLiveKey } from "@/lib/eventsLiveKey";

/** A stable identity for a set of events, so the frozen list only rebuilds when
 *  the feed actually changes rather than on every render of the parent. */
function eventsKey(events: readonly CatchUpEventInput[]): string {
  return events.map((e) => e.id).join("|");
}

export function EventCatchUp({
  events,
  className = "",
}: {
  events: readonly CatchUpEventInput[];
  /** Spacing/gutter from the host page. Applied to the card itself rather than
   *  to a wrapper, so a card with nothing to ask about takes up NO space —
   *  an empty wrapper still carries its own margin, which on the Profile spine
   *  reads as a gap where a card failed to load. */
  className?: string;
}) {
  const { userPriorities } = useSession();
  const key = eventsKey(events);
  /**
   * The foci as a STRING, never the array.
   *
   * `userPriorities` is rebuilt by its provider on every render, so depending on
   * `collectionFoci` by identity re-ran the effect, which set state, which
   * re-rendered, which re-ran the effect: an infinite loop that hung the render
   * rather than showing anything. What the effect actually cares about is which
   * foci they are, and that is a string.
   */
  const fociKey = userPriorities.collectionFoci.join(",");
  // Evo (2026-10-20): with the device-wide switch on, every featured species
  // brings its whole line, so the card can tick a line in one commit. The
  // switch is part of the frozen set's identity: flipping it rebuilds the
  // list, which is the one thing a flip is for.
  const [evoLines] = useEvoLines();

  // Computed once per event set — see the note about freezing above. The dex is
  // read at that moment; ticks made here are tracked locally instead.
  const [entries, setEntries] = useState<CatchUpEntry[]>([]);
  useEffect(
    () => {
      setEntries(
        eventCatchUps(
          evoLines ? events.map((e) => ({ ...e, spawns: expandSpawnsToLines(e.spawns ?? []) })) : events,
          fociKey ? (fociKey.split(",") as typeof userPriorities.collectionFoci) : [],
          // A form-scoped gap (a costume/regional linked to the event) is
          // checked against the FORM's own dex row, and gated on the form's
          // release + shiny art, exactly like the Calendar/radar gap strips.
          // Dropping formId here read the BASE row instead, so a costume gap
          // vanished whenever base was caught and lingered when it wasn't.
          (variant, speciesId, formId) =>
            getDexEntryState(variant, speciesId, formId)?.state === "present",
          (variant, speciesId, formId) =>
            formId
              ? isEventFormAvailable(variant, formId, speciesId)
              : isVariantAvailable(variant, speciesId),
          { now: Date.now() },
        ),
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key, fociKey, evoLines],
  );

  if (entries.length === 0) return null;

  return (
    // The heading used to be a v2-eyebrow PILL. A pill says "this is the top of
    // a page"; three blocks down a spine it just said "this card was built by
    // someone else", which is what it was. Same InfoBlock as every other block
    // now, which is also where its missing spine dot comes from.
    <InfoBlock
      icon={CalendarCheck}
      title="Quick update"
      subtitle="Just finished, and still missing from your Nexus Dex. Tap to quick update it."
      trailing={<EvoLinesToggle testId="event-catch-up-evo" />}
      className={className}
      data-testid="section-event-catch-up"
    >
      <div className="space-y-4">
        {entries.map((entry) => (
          <CatchUpEventBlock key={entry.eventId} entry={entry} />
        ))}
      </div>
    </InfoBlock>
  );
}

/**
 * NOTHING IS WRITTEN UNTIL YOU SAY SO.
 *
 * A tap used to be the write. That is the fastest possible version and the
 * wrong one for a grid of small tiles inside a scrolling page: a stray thumb on
 * the way past adds a Pokémon to the dex every other surface in this app counts
 * from, silently, and the trainer has no reason to look. Whether they would
 * ever notice is exactly the problem.
 *
 * So a tap SELECTS, and one button commits the lot. The cost is a single extra
 * tap for the whole card rather than a confirm per species, which keeps the
 * thing that made this worth building — three shinies is four taps, against a
 * trip through the Dex tab per Pokémon.
 *
 * And a commit is still reversible three ways: the toast carries an Undo, the
 * card keeps one, and any added tile can be tapped to take just that one back.
 * All of them restore the exact prior state, cascade included.
 *
 * The mechanic itself lives in useGapCommit (2026-08-24), shared with the
 * Profile radar and Calendar gap strips so the three surfaces cannot drift.
 */
function CatchUpEventBlock({ entry }: { entry: CatchUpEntry }) {
  const { tileState, tap, commit, undoAll, selectedCount, addedCount } = useGapCommit(
    entry.gaps,
  );

  return (
    <div data-testid={`event-catch-up-${entry.eventId}`}>
      <p className="text-[12px] font-bold" style={{ color: "var(--text-ink-bright)" }}>
        {entry.title}
      </p>
      {/* FIVE ACROSS, CENTRED — see GAP_TILE_ROW_CLASS. */}
      <ul className={`mt-2 ${GAP_TILE_ROW_CLASS}`}>
        {entry.gaps.map((g) => {
          return (
            <li
              key={`${g.variant}:${g.speciesId}:${g.formId ?? ""}`}
              className="min-w-0"
              style={GAP_TILE_CELL_STYLE}
            >
              <CatchUpTile
                variant={g.variant}
                speciesId={g.speciesId}
                formId={g.formId}
                state={tileState(g)}
                onToggle={() => tap(g)}
                testId={`event-catch-up-tile-${entry.eventId}-${g.variant}-${g.speciesId}${g.formId ? `-${g.formId}` : ""}`}
              />
            </li>
          );
        })}
      </ul>

      {/* The commit. Only ever present when there is something to commit, so
          the card is inert until the trainer has actually chosen something. */}
      {selectedCount > 0 && (
        <button
          type="button"
          onClick={commit}
          data-testid={`event-catch-up-commit-${entry.eventId}`}
          className="mt-2 w-full rounded-full py-2 text-[12px] font-bold"
          style={{
            background: "var(--accent-fill)",
            color: "var(--text-on-accent)",
            minHeight: 40,
          }}
        >
          Add {selectedCount} to my Nexus Dex
        </button>
      )}

      {/* After a commit, the way back stays on the card rather than living only
          in a toast that has already gone. */}
      {selectedCount === 0 && addedCount > 0 && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-[10px]" style={{ color: "var(--text-tertiary)" }}>
            {addedCount} added. Tap one to take it back.
          </p>
          <button
            type="button"
            onClick={undoAll}
            data-testid={`event-catch-up-undo-${entry.eventId}`}
            className="shrink-0 rounded-full px-3 py-1 text-[10px] font-bold"
            style={{
              background: "var(--surface-sunken)",
              color: "var(--text-secondary)",
              border: "1px solid var(--border-subtle)",
              minHeight: 32,
            }}
          >
            Undo all
          </button>
        </div>
      )}

    </div>
  );
}

/**
 * FIVE ACROSS, CENTRED — the gap strip's row, shared by every surface that
 * renders <CatchUpTile> (this card, the Profile raid radar, the events pages'
 * EventDexGaps) so the four cannot drift apart.
 *
 * Five across, because a wrapping row of fixed-width tiles left two orphans
 * hanging under a row of four on a phone, which reads as a layout accident
 * rather than a list. Each cell takes exactly the width a `grid-cols-5` track
 * gave it, so nothing about tile or sprite sizing changes.
 *
 * CENTRED, because a short row is the common case here, not the exception: a
 * group is usually two or three species, and left-aligned they hung off the
 * card's left edge under a centred heading (2026-08-31 device report). Flex,
 * not grid, is what makes that possible — grid tracks are fixed, so the tiles
 * always sat in columns 1..n.
 *
 * The half-pixel shaved off each cell is anti-wrap insurance: at exactly
 * (100% - gaps) / 5 a sub-pixel rounding up would drop the fifth tile onto a
 * line of its own, and centring would then make that obvious rather than
 * invisible.
 */
export const GAP_TILE_ROW_CLASS = "flex flex-wrap justify-center gap-1.5";
/** Cell width for GAP_TILE_ROW_CLASS: one `grid-cols-5` track (gap-1.5 = 4 ×
 *  0.375rem between five tiles), less the anti-wrap hair. */
export const GAP_TILE_CELL_STYLE = {
  flex: "0 0 calc((100% - 1.5rem) / 5 - 0.5px)",
} as const;

/**
 * One species, one variant, three states you can tell apart at a glance.
 *
 * IDLE is a silhouette, which is what "you don't have this" looks like
 * everywhere else in the app. SELECTED is the real sprite with an accent ring —
 * it says "this is going in" without claiming it already has. ADDED is the
 * sprite with a solid tick.
 *
 * Selected and added deliberately do NOT look the same. A trainer who taps four
 * tiles and gets called away has to be able to come back and see whether they
 * pressed the button, and "the sprite lit up" cannot answer that on its own.
 *
 * NO FIXED WIDTH. The tile fills whatever cell the row gives it and the
 * sprite fills the tile, so five fit across a small phone and a large one
 * without a breakpoint or a magic number. The variant badge sits INSIDE the
 * tile for the same reason: at five across, a badge overhanging the corner
 * would sit on its neighbour.
 *
 * Exported: the Catch/Raid radar gap strips replicate this exact mechanic
 * (2026-08-24 ask), so they render the same tile rather than a lookalike.
 */
export function CatchUpTile({
  variant,
  speciesId,
  state,
  onToggle,
  testId,
  formId,
}: {
  variant: RaidDexVariant;
  speciesId: number;
  state: TileState;
  onToggle: () => void;
  testId: string;
  /** Form-scoped gap (a costume/regional linked to the event): the tile shows
   * the FORM's name and art, and its writes target the form's dex row. */
  formId?: string;
}) {
  // A form tile draws the form itself: its display name under the sprite and
  // its own art (the WCS costume, not base Pikachu). Falls back to the
  // species rendering when the form is unknown to this build's catalogue.
  const form = formId
    ? FORM_GROUPS_BY_FORM_ID.get(formId)?.forms.find((f) => f.formId === formId)
    : undefined;
  // A Mega-scoped copy (Shiny Mega, 100% Mega …, 2026-09-03) draws the Mega
  // form's own art and name; its form id is not a catalogue form.
  const megaForm = formId && !form ? megaFormForFormId(speciesId, formId) : null;
  // A Dynamax-scoped copy (Shiny Dynamax, 100% Dynamax …, 2026-09-04) is the
  // species' own art under a Dynamax name; its writes land on the species
  // page's Dynamax view.
  const dynamax = formId === DYNAMAX_FORM_ID;
  const displayName = form
    ? `${speciesName(speciesId)} (${form.displayName})`
    : megaForm
      ? megaForm.name
      : dynamax
        ? `Dynamax ${speciesName(speciesId)}`
        : speciesName(speciesId);
  // The form's colour art as a CANDIDATE CHAIN, never a single stored path
  // (2026-09-14 owner report: regional tiles on the Profile drew nothing).
  // A regional's stored spritePath is PokéAPI's pixel file
  // (pokeapi/pokemon/10091.png), which our mirror does not carry: the tile
  // put that one URL in an <img> and showed the broken-image glyph. The
  // registry chain (speciesFormArtCandidates) walks HOME render -> official
  // art -> pixel -> base species, the same resolver the Shiny Hunt and raid
  // tiles use, so a Galarian or Alolan gap now draws its proper render and a
  // costume whose icon has not landed falls back to base Pikachu. Megas keep
  // their own chains.
  const formCandidates: string[] | undefined = form
    ? speciesFormArtCandidates(speciesId, form.formId, { shiny: variant === "shiny" })
    : megaForm
      ? megaForm.formId
        ? megaFormSpriteCandidates(MEGA_EXTRA_FORMS.find((f) => f.formId === megaForm.formId)!)
        : megaGmaxSpriteCandidates("mega", speciesId)
      : variant === "mega"
        ? megaGmaxSpriteCandidates("mega", speciesId)
        : undefined;
  const label = `${raidVariantLabel(variant)} ${displayName}`;
  const live = state !== "idle";
  const added = state === "added";
  // The atlas silhouette for this tile's shape, when one exists: a Mega's own
  // outline (base or X/Y), Armored Mewtwo's, or the Mega shape on the whole-
  // species MEGA tile. Costumes / regionals have no tile and read undefined.
  const silhouetteSlug: string | undefined = megaForm
    ? megaGmaxAtlasFormSlug("mega", speciesId, megaForm.formId ?? undefined) ?? undefined
    : form
      ? atlasFormSlugForFormId(speciesId, form.formId)
      : variant === "mega"
        ? megaGmaxAtlasFormSlug("mega", speciesId) ?? undefined
        : undefined;
  // An idle tile is "not caught": it draws the standard accent silhouette
  // (2026-09-07 owner report: Mega / Armored gap tiles were near-black flat
  // art in the dark theme). Every idle tile, forms included (2026-09-18 owner
  // report: Alolan and Galarian gaps on the Profile were the only colour
  // sprites in rows of green outlines). A form with its own atlas tile draws
  // that shape; a costume or regional, which has none, draws the base species'
  // outline and says which form it is in the caption. The form's colour art
  // is for the live (chosen / added) states only.
  const idleArtIsSilhouette = !live;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-pressed={live}
      aria-label={
        added
          ? `${label}, in your Nexus Dex. Tap to take it back`
          : state === "selected"
            ? `${label}, chosen. Tap to unchoose`
            : `${label}, tap to choose`
      }
      data-testid={testId}
      data-state={state}
      className="relative w-full rounded-xl p-1 hover-elevate flex flex-col items-center"
      style={{
        background: live ? "var(--surface-raised)" : "var(--surface-sunken)",
        border: `1px solid ${live ? "var(--accent-fill)" : "var(--border-subtle)"}`,
        // The chosen-but-not-written state gets the ring; only a real write
        // gets the filled badge below.
        boxShadow: state === "selected" ? "0 0 0 2px var(--accent-fill-glow)" : undefined,
      }}
    >
      {/* A square that follows the column width, with the sprite filling it. */}
      <span className="relative block w-full aspect-square">
        {idleArtIsSilhouette ? (
          <PokemonSilhouette id={speciesId} fill caught={false} shiny={false} formSlug={silhouetteSlug} />
        ) : (
          // CaughtSprite walks the candidate chain on <img> error, so a
          // missing render never leaves a blank. A live tile shows the art in
          // colour; an idle costume / regional (no atlas tile) shows it
          // greyed, at an opacity that still reads on the dark surface.
          <span
            className="absolute inset-0"
            style={live ? undefined : { filter: "grayscale(1)", opacity: 0.55 }}
            data-testid={`${testId}-art`}
          >
            <CaughtSprite
              id={speciesId}
              fill
              shiny={variant === "shiny"}
              formSpriteCandidates={formCandidates}
              formSlug={silhouetteSlug}
            />
          </span>
        )}
        {/* The badge wears its dex's colour, the same hue as the variant pill
            row on the Pokédex (2026-10-12 owner ask), so a row of mixed tiles
            sorts itself by colour without a heading per variant. An ADDED
            tile keeps the accent fill: that state is about the write, not
            the dex. */}
        <span
          className="absolute top-0 right-0 text-[7px] font-extrabold uppercase tracking-tight px-1 rounded-full inline-flex items-center gap-0.5"
          style={
            added
              ? { background: "var(--accent-fill)", color: "var(--text-on-accent)" }
              : isDexVariantKey(variant)
                ? {
                    background: variantAlpha(variant, 0.16),
                    border: `1px solid ${variantAlpha(variant, 0.6)}`,
                    color: variantColor(variant),
                  }
                : { background: "var(--surface-overlay)", color: "var(--text-secondary)" }
          }
          data-testid={`${testId}-badge`}
        >
          {added && <Check className="w-2 h-2" aria-hidden />}
          {raidVariantLabel(variant)}
        </span>
      </span>
      <span className="mt-0.5 w-full truncate text-center text-[7px] leading-tight text-[var(--text-tertiary)]">
        {megaForm ? megaForm.name : dynamax ? "Dynamax" : speciesName(speciesId)}
      </span>
      {form && (
        // The form under the species, on its own line: with the base outline
        // above, this line is what tells an Alolan Meowth from a Kanto one.
        <span
          className="w-full truncate text-center text-[7px] leading-tight uppercase tracking-tight text-[var(--text-ink-faint)]"
          data-testid={`${testId}-form`}
        >
          {form.displayName}
        </span>
      )}
    </button>
  );
}

/**
 * The card, fetching its own events.
 *
 * Two pages want this (Calendar, where it is the counterpart to the upcoming
 * list, and Profile, where it is the prompt you see without going looking) and
 * react-query serves the second from the first's cache, so a self-fetching
 * section costs nothing and keeps the two call sites to one line each.
 *
 * Renders NOTHING when there is nothing to ask about — no empty state, no
 * "you're all caught up" card. A permanent card that usually has no question in
 * it is exactly what got the old Next-up card deleted from the Profile spine.
 */
export function EventCatchUpSection({ className }: { className?: string }) {
  const events = useRecentEvents();
  if (events.length === 0) return null;
  return <EventCatchUp events={events} className={className} />;
}

interface RecentEventApi {
  id: string;
  name?: string;
  heading?: string;
  start?: string;
  end?: string;
  spawns?: CatchUpEventInput["spawns"];
  category?: string;
  rawType?: string;
}

/** The feed's `recent` bucket — events that ended inside the last week. */
function useRecentEvents(): CatchUpEventInput[] {
  const { data } = useQuery<{ recent?: RecentEventApi[] }>({
    queryKey: [eventsLiveKey()],
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
  return useMemo(
    () =>
      (data?.recent ?? []).map((e) => ({
        id: e.id,
        // Feed text is scraped HTML and arrives carrying entities; decode it
        // here for the same belt-and-braces reason the Calendar does.
        title: decodeEventTextOptional(e.heading) ?? decodeEventTextOptional(e.name) ?? "Event",
        start: e.start,
        end: e.end,
        spawns: e.spawns,
        // Rides through to the cross-reference: a finished Max Monday implies
        // a Dynamax gap check for its featured species.
        category: e.category,
        rawType: e.rawType,
      })),
    [data?.recent],
  );
}
